import { Component, h, createRef, RefObject } from 'preact';
import { Subject, Subscription } from 'rxjs';

import { ReqState } from '../../../../../types';
import * as Validators from '../../../../utils/forms/validators';
import { Inquiry, InquiryFTF } from '../../../../../types/inquiries';

import { inquiryService } from '../../../../utils/inquiry.service';
import { hopplerUpdateFormFieldValue, HopplerFieldUpdateOptions } from '../../../../utils/forms/hoppler-linkstate';
import { Form } from '../../../../utils/forms/form';
import { AddClientModal } from './add-client-modal.component';
import { createFormFields, createFtfObject } from './form-helpers';
import { FastTrackModalMode } from './mode';

import getStore from '../../../store';
import * as InquiriesActions from '../../../reducers/inquiries/inquiries.actions';

interface AddClientModalHostProps {
  onProceed: () => void;
  onClose: () => void;

  mode: FastTrackModalMode;
  inquiry?: Inquiry;
  ftf?: InquiryFTF;
}

interface AddClientModalHostState {
  form?: Form;
  submitState?: ReqState;
}

const store = getStore();

export class AddClientModalHost extends Component<AddClientModalHostProps, AddClientModalHostState> {
  form$ = new Subject<Form>();
  formScrollerRef = createRef<HTMLDivElement>();
  inputRefs: { [key: string]: RefObject<HTMLInputElement> } = {};

  subscriptions: Subscription[] = [];

  constructor(props) {
    super(props);

    this.setupForm();
  }

  setupForm() {
    const fields = createFormFields(this.props.ftf);

    for (let key in fields) {
      this.inputRefs[key] = createRef<HTMLInputElement>();
    }

    const form = new Form(fields, { pristineUntilEdited: true });
    this.setState({ form });

    this.subscriptions.push(Form.bind(this, this.form$));
  }

  onValidationScroll(offsetTop: number) {
    // console.log('offsetTop:', offsetTop);
    const scroller = this.formScrollerRef.current;
    const scrollerRect = scroller.getBoundingClientRect();
    // 64px extra vertical space to allow for input labels and spacing
    const targetScroll = offsetTop - scrollerRect.top - 64;

    scroller.scrollTo({ top: targetScroll, left: 0, behavior: 'smooth' });
  }

  scrollToFirstError() {
    const form = this.state.form;
    const firstInvalidKey = form.getFirstInvalidFieldName();

    if (firstInvalidKey) {
      const ref = this.inputRefs[firstInvalidKey];
      let current = ref.current;
      // console.log('First invalid, current:', firstInvalidKey, current);

      // If the element is a Preact component, we need to reach inside and get
      // the actual DOM element
      if (current['base']) {
        current = current['base'];
      }

      this.onValidationScroll(current.offsetTop);
    }
  }

  onModalFieldChange(fieldName: string, value: any, options?: HopplerFieldUpdateOptions) {
    // console.log(fieldName, '=', value);
    let form = this.state.form;
    
    if (fieldName === 'division') {
      // Use recursion so *all* necessary downstream cleanup is executed
      form = this.onModalFieldChange('type', null, { automated: true });
      form = this.onModalFieldChange('amenities', [], { automated: true });
      form = this.onModalFieldChange('furnishing', [], { automated: true });
    } else if (fieldName === 'type') {
      form = this.onModalFieldChange('locations', [], { automated: true });
    }

    return hopplerUpdateFormFieldValue(this.form$, form, fieldName, value);
  }

  async onSubmit() {
    Validators.touchEntireForm(this.form$, this.state.form);
    const form = this.state.form;
    const mode = this.props.mode;
    
    if (!form.isValid) {
      this.scrollToFirstError();
    } else {
      const lead = createFtfObject(form);
      try {
        this.setState({ submitState: ReqState.IN_PROGRESS });

        if (mode === FastTrackModalMode.ADD_CLIENT) {
          await inquiryService.addClient(lead);
        } else {
          // Ensure that we don't accidentally overwrite anything
          delete lead.source;
          delete lead.listings;
          const ftf = lead as InquiryFTF;

          const inquiry = this.props.inquiry;
          const inquiryCode = inquiry.inquiryCode;
          await inquiryService.updateInquiryFtf(inquiryCode, ftf);

          store.dispatch(InquiriesActions.reloadSelectedInquiry(inquiry));
        }

        this.setState({ submitState: ReqState.SUCCESS });
      } catch (err) {
        this.setState({ submitState: ReqState.FAILED });
      }
    }
  }

  render(props: AddClientModalHostProps, state: AddClientModalHostState) {
    return (
      <AddClientModal
        form={ state.form }
        scrollerRef={ this.formScrollerRef }
        inputRefs={ this.inputRefs }
        submitState={ state.submitState }
        mode={ props.mode }
        inquiry={ props.inquiry }
        
        onFieldChange={ (field, value) => this.onModalFieldChange(field, value) }
        onSubmit={ () => this.onSubmit() }
        onProceed={ props.onProceed }
        onClose={ props.onClose } />
    );
  }

  componentWillUnmount() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions = [];
  }
}